import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Send, Paperclip, X, Loader2, Upload,
  MessageSquare, FolderOpen, Download, FileText,
  Image, Film, Clock, User, Shield
} from 'lucide-react';
import { useAuth } from '../../components/auth/AuthContext';
import { servicesService } from '../../services/ServicesService';

const POLL_INTERVAL = 5000;

const TicketDetailPage = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('chat');

  // Chat state
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Materials upload state
  const [materialFiles, setMaterialFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const messagesEndRef = useRef(null);
  const lastMessageTime = useRef(null);
  const pollRef = useRef(null);

  // Fetch ticket and messages
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ticketRes, messagesRes] = await Promise.all([
          servicesService.getTicket(ticketId),
          servicesService.getMessages(ticketId),
        ]);
        setTicket(ticketRes.data);
        setMessages(messagesRes.data || []);

        const msgs = messagesRes.data || [];
        if (msgs.length > 0) {
          lastMessageTime.current = msgs[msgs.length - 1].created_at;
        }
      } catch (err) {
        console.error('Erro ao buscar ticket:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [ticketId]);

  // Polling for new messages
  useEffect(() => {
    if (activeTab !== 'chat') return;

    pollRef.current = setInterval(async () => {
      try {
        const res = await servicesService.getMessages(ticketId, lastMessageTime.current);
        const newMsgs = res.data || [];
        if (newMsgs.length > 0) {
          setMessages(prev => [...prev, ...newMsgs]);
          lastMessageTime.current = newMsgs[newMsgs.length - 1].created_at;
        }
      } catch (err) {
        // Silent fail on polling
      }
    }, POLL_INTERVAL);

    return () => clearInterval(pollRef.current);
  }, [ticketId, activeTab]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    setIsSending(true);

    try {
      const formData = new FormData();
      formData.append('content', messageText.trim());

      const res = await servicesService.sendMessage(ticketId, formData);
      setMessages(prev => [...prev, res.data]);
      lastMessageTime.current = res.data.created_at;
      setMessageText('');
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleMaterialFileAdd = (e) => {
    // Snapshot dos arquivos ANTES de qualquer reset — o setState do React e
    // assincrono e roda depois de e.target.value='', que zera e.target.files.
    const newFiles = Array.from(e.target.files || []);
    e.target.value = '';
    if (newFiles.length === 0) return;
    setMaterialFiles(prev => [...prev, ...newFiles]);
  };

  const handleMaterialUpload = async () => {
    console.log('[UPLOAD-DEBUG] handleMaterialUpload chamado; materialFiles=', materialFiles.length);
    if (materialFiles.length === 0) return;
    setIsUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      materialFiles.forEach(file => formData.append('files', file));
      console.log('[UPLOAD-DEBUG] enviando sendMessage para ticket', ticketId);
      const res = await servicesService.sendMessage(ticketId, formData);
      console.log('[UPLOAD-DEBUG] sendMessage retornou:', res);
      if (res?.data) {
        setMessages(prev => [...prev, res.data]);
        lastMessageTime.current = res.data.created_at;
      }
      setMaterialFiles([]);
    } catch (err) {
      console.error('Erro ao enviar materiais:', err);
      const detail = err?.response?.data?.detail || err?.message || 'Falha ao enviar arquivo. Tente novamente.';
      setUploadError(typeof detail === 'string' ? detail : 'Falha ao enviar arquivo. Tente novamente.');
    } finally {
      setIsUploading(false);
    }
  };

  // Collect all attachments from messages + ticket
  const allAttachments = [
    ...(ticket?.attachments || []).map(a => ({ ...a, source: 'Solicitacao inicial' })),
    ...messages.flatMap(m => (m.attachments || []).map(a => ({ ...a, source: m.sender_name }))),
  ];

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return Image;
    if (mimeType?.startsWith('video/')) return Film;
    return FileText;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Ticket nao encontrado</p>
          <button onClick={() => navigate('/services')} className="text-amber-600 font-medium hover:underline">
            Voltar para Serviços
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-20 pt-4 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/60 backdrop-blur-md rounded-3xl border border-gray-200/50 px-6 shadow-sm">
            <div className="flex items-center justify-between h-16">
              <button
                onClick={() => navigate('/services')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium hidden sm:inline">Voltar</span>
              </button>
              <div className="text-center">
                <h1 className="text-sm font-bold text-gray-900">
                  {servicesService.helpers.getServiceLabel(ticket.service_type)}
                </h1>
                <span className={`inline-block mt-0.5 px-2 py-0.5 text-xs font-medium rounded-full ${servicesService.helpers.getStatusColor(ticket.status)}`}>
                  {servicesService.helpers.getStatusLabel(ticket.status)}
                </span>
              </div>
              <div className="w-20 text-right text-xs text-gray-400">
                {new Date(ticket.created_at).toLocaleDateString('pt-BR')}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="w-full max-w-4xl mx-auto px-4 mt-4">
        <div className="flex w-full bg-white rounded-xl border border-gray-200 p-1">
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === 'chat'
                ? 'bg-amber-100 text-amber-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <MessageSquare className="w-4 h-4 flex-shrink-0" />
            <span>Chat</span>
          </button>
          <button
            onClick={() => setActiveTab('materials')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === 'materials'
                ? 'bg-amber-100 text-amber-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FolderOpen className="w-4 h-4 flex-shrink-0" />
            <span>Materiais ({allAttachments.length})</span>
          </button>
        </div>
      </div>

      {/* Encerrado Banner */}
      {ticket.status === 'encerrado' && ticket.close_reason && (
        <div className="w-full max-w-4xl mx-auto px-4 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <p className="text-sm font-semibold text-red-700 mb-1">Serviço encerrado</p>
            <p className="text-sm text-red-600">{ticket.close_reason}</p>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 max-w-4xl mx-auto px-4 mt-4 pb-4 w-full">

        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className="bg-white rounded-2xl border border-gray-200 flex flex-col" style={{ height: ticket.status === 'encerrado' ? 'calc(100vh - 290px)' : 'calc(100vh - 220px)' }}>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Nenhuma mensagem ainda.</p>
                  <p className="text-xs mt-1">O chat sera ativado quando um administrador interagir com seu ticket.</p>
                </div>
              )}

              {messages.map((msg) => {
                const isUser = msg.sender_role === 'user';
                return (
                  <div key={msg.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] ${isUser ? 'order-2' : 'order-1'}`}>
                      <div className={`flex items-center gap-2 mb-1 ${isUser ? 'justify-end' : 'justify-start'}`}>
                        {!isUser && <Shield className="w-3 h-3 text-amber-500" />}
                        <span className="text-xs font-medium text-gray-500">{msg.sender_name}</span>
                        <span className="text-xs text-gray-300">
                          {new Date(msg.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <div className={`rounded-2xl px-4 py-3 ${
                        isUser
                          ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}>
                        {msg.content && <p className="text-sm whitespace-pre-wrap">{msg.content}</p>}
                        {msg.attachments?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {msg.attachments.map((att, idx) => {
                              const AttIcon = getFileIcon(att.mime_type);
                              return (
                                <a
                                  key={idx}
                                  href={att.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 ${
                                    isUser ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-white text-gray-600 hover:bg-gray-50'
                                  } transition-colors`}
                                >
                                  <AttIcon className="w-4 h-4 flex-shrink-0" />
                                  <span className="truncate">{att.file_name}</span>
                                  <Download className="w-3 h-3 flex-shrink-0 ml-auto" />
                                </a>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Input */}
            {servicesService.helpers.isTicketClosed(ticket.status) ? (
              <div className="border-t border-gray-200 p-4 text-center text-sm text-gray-400">
                Este serviço foi {ticket.status === 'encerrado' ? 'encerrado' : 'concluído'}. O chat está desativado.
              </div>
            ) : (
            <div className="border-t border-gray-200 p-4">
              <div className="flex items-end gap-2">
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Digite sua mensagem..."
                  rows={1}
                  className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400 transition-all"
                  style={{ minHeight: '42px', maxHeight: '120px' }}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={isSending || !messageText.trim()}
                  className="p-2.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl hover:from-amber-500 hover:to-orange-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isSending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </div>
            </div>
            )}
          </div>
        )}

        {/* Materials Tab */}
        {activeTab === 'materials' && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            {/* Upload area */}
            {!servicesService.helpers.isTicketClosed(ticket.status) && (
              <div className="mb-6">
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 hover:border-amber-300 rounded-xl py-6 px-4 cursor-pointer transition-colors bg-gray-50 hover:bg-amber-50/30">
                  <Upload className="w-6 h-6 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-500">Clique para anexar arquivos</span>
                  <input type="file" multiple onChange={handleMaterialFileAdd} className="hidden" />
                </label>

                {materialFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {materialFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-2">
                        <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate flex-1">{file.name}</span>
                        <span className="text-xs text-gray-400">{formatFileSize(file.size)}</span>
                        <button onClick={() => setMaterialFiles(prev => prev.filter((_, i) => i !== idx))}>
                          <X className="w-4 h-4 text-gray-400 hover:text-red-500" />
                        </button>
                      </div>
                    ))}
                    {uploadError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl px-4 py-2">
                        {uploadError}
                      </div>
                    )}
                    <button
                      onClick={handleMaterialUpload}
                      disabled={isUploading}
                      className="w-full py-2.5 text-sm font-medium text-white bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 rounded-xl transition-all disabled:opacity-50"
                    >
                      {isUploading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" /> Enviando...
                        </span>
                      ) : (
                        `Enviar ${materialFiles.length} arquivo${materialFiles.length > 1 ? 's' : ''}`
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Attachments list */}
            {allAttachments.length === 0 && materialFiles.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <FolderOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Nenhum material compartilhado ainda.</p>
              </div>
            ) : allAttachments.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {allAttachments.map((att, idx) => {
                  const AttIcon = getFileIcon(att.mime_type);
                  return (
                    <a
                      key={idx}
                      href={att.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 bg-gray-50 hover:bg-gray-100 rounded-xl px-4 py-3 transition-colors"
                    >
                      <div className="w-10 h-10 bg-white rounded-lg border border-gray-200 flex items-center justify-center flex-shrink-0">
                        <AttIcon className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">{att.file_name}</p>
                        <p className="text-xs text-gray-400">
                          {formatFileSize(att.file_size)} {att.source && `- ${att.source}`}
                        </p>
                      </div>
                      <Download className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketDetailPage;
